import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { KeyRound, Link2, ShieldCheck, UserCircle, Facebook, MessageCircle, MessageSquare, BookOpen } from 'lucide-react';

const SettingsPage = () => {
  const cards = [
    { title: 'Profile', description: 'Manage your profile information', href: '/settings/profile', icon: UserCircle, tone: '#0ea5e9' },
    { title: 'API Token', description: 'Manage API tokens for integrations', href: '/settings/api-token', icon: KeyRound, tone: '#10b981' },
    { title: 'Template API Docs', description: 'Developer docs for template send integration', href: '/settings/template-api-docs', icon: BookOpen, tone: '#0f766e' },
    { title: 'Webhook', description: 'Configure webhook callbacks', href: '/settings/webhook', icon: Link2, tone: '#f97316', comingSoon: true },
    { title: 'WhatsApp Profile', description: 'Manage WhatsApp profile settings', href: '/whatsappProfile', icon: MessageCircle, tone: '#22c55e' },
    { title: 'Facebook Details', description: 'Manage Facebook integration settings', href: '/facebookDetails', icon: Facebook, tone: '#2563eb' },
    { title: 'Bot Messages', description: 'Manage bot trigger replies', href: '/settings/bot-messages', icon: MessageSquare, tone: '#0f766e' },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          mb: 3,
          p: { xs: 2.5, md: 3 },
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          color: 'primary.contrastText',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShieldCheck size={24} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Settings
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Manage integrations, security, and profile preferences.
            </Typography>
          </Box>
        </Stack>
      </Card>

      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                ...(card.comingSoon
                  ? {
                      opacity: 0.65,
                      cursor: 'default',
                    }
                  : {
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 14px 30px rgba(15, 23, 42, 0.12)',
                      },
                    }),
              }}
            >
              <CardActionArea href={card.comingSoon ? undefined : card.href} sx={{ height: '100%' }} disabled={card.comingSoon}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: `${card.tone}1A`,
                        color: card.tone,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <card.icon size={20} />
                    </Box>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h6">{card.title}</Typography>
                        {card.comingSoon && (
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 999,
                              bgcolor: 'rgba(15, 23, 42, 0.08)',
                              color: 'text.secondary',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                            }}
                          >
                            Coming soon
                          </Box>
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {card.description}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SettingsPage;
